export const PAYMENT_SCREENSHOT_TARGET_BYTES = 300 * 1024;
/** Server rejects anything above 1.5 MB. */
export const PAYMENT_SCREENSHOT_MAX_BYTES = 1.5 * 1024 * 1024;

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_DIMENSION = 1600;
const MIN_DIMENSION = 640;
const QUALITY_STEPS = [0.82, 0.72, 0.62, 0.52, 0.42];

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error(
          "Could not read this image. Please upload a JPG, PNG or WebP screenshot.",
        ),
      );
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

function jpegName(name) {
  const base = String(name || "payment").replace(/\.[^.]+$/, "") || "payment";
  return `${base.replace(/[^\w.\- ]+/g, "_").slice(0, 120)}.jpg`;
}

/**
 * Re-encodes an image as JPEG, shrinking quality then dimensions until it is about
 * {@link PAYMENT_SCREENSHOT_TARGET_BYTES}. Small JPEG/PNG/WebP files are returned untouched.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressImageFile(
  file,
  targetBytes = PAYMENT_SCREENSHOT_TARGET_BYTES,
) {
  if (ACCEPTED_TYPES.includes(file.type) && file.size <= targetBytes) return file;

  const img = await loadImage(file);
  let scale = Math.min(
    1,
    MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight),
  );
  let best = null;

  for (;;) {
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // JPEG has no alpha; paint white so transparent PNG areas do not turn black.
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    for (const quality of QUALITY_STEPS) {
      const blob = await canvasToBlob(canvas, quality);
      if (!blob) break;
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= targetBytes) {
        return new File([blob], jpegName(file.name), { type: "image/jpeg" });
      }
    }

    if (Math.max(width, height) <= MIN_DIMENSION) break;
    scale *= 0.75;
  }

  if (!best) {
    if (ACCEPTED_TYPES.includes(file.type) && file.size <= PAYMENT_SCREENSHOT_MAX_BYTES)
      return file;
    throw new Error("Could not process this image. Please try a different screenshot.");
  }
  return new File([best], jpegName(file.name), { type: "image/jpeg" });
}
